import { MailWarning, ArrowRight } from 'lucide-react';

import { useEmailVerification } from '@/hooks/useEmailVerification';
import { useVerifyEmail } from './VerifyEmailProvider';

/**
 * Persistent "verify your email" banner (NEA-5773). Renders in AppLayout's
 * <main> alongside the connection-health banners while the signed-in user's
 * primary email is unverified, and stays until verification clears it (it's a
 * security gap, so it is intentionally not dismissible). Visually mirrors the
 * `HealthBanner` "warning" tone for one consistent banner rhythm; the CTA opens
 * the shared verification dialog rather than navigating.
 */
export function VerifyEmailBanner() {
	const { needsVerification, email } = useEmailVerification();
	const { openVerifyDialog } = useVerifyEmail();

	if (!needsVerification) return null;

	return (
		<div className="w-full border-b border-warning/30 bg-warning-soft text-warning-soft-foreground" data-banner-id="verify-email">
			<div className="mx-auto flex max-w-[1100px] items-start gap-3 px-6 py-2.5 md:px-10">
				<MailWarning className="mt-0.5 h-4 w-4 shrink-0 text-warning-soft-foreground" />

				<div className="min-w-0 flex-1">
					<div className="text-sm font-semibold text-warning-soft-foreground">Verify your email address</div>
					<p className="mt-0.5 text-xs text-muted-foreground">
						{email ? (
							<>
								Confirm <span className="font-medium">{email}</span> to secure your account and go live.
							</>
						) : (
							'Confirm your email address to secure your account and go live.'
						)}
					</p>
				</div>

				<div className="flex shrink-0 items-center self-center">
					<button
						type="button"
						onClick={openVerifyDialog}
						className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-semibold text-warning-soft-foreground hover:underline"
					>
						Verify now <ArrowRight className="h-3.5 w-3.5" />
					</button>
				</div>
			</div>
		</div>
	);
}
